import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ethers } from 'ethers';
import { 
    Coins, 
    CheckCircle, 
    AlertCircle, 
    Loader, 
    Clock,
    Shield,
    TrendingUp,
    ExternalLink,
    RefreshCw,
    Wallet,
    ArrowDownUp,
    X,
    DollarSign,
    Zap,
    Info
} from 'lucide-react';
import './TokenClaim.css';

// Import ABIs
import UserVestingABI from '../contracts/abis/UserVesting.json';
import TPhileTokenABI from '../contracts/abis/TPhileToken.json';

// Import token configuration
import { ACTIVE_NETWORK, TOKEN_METADATA, getCurrentNetwork, switchToNetwork } from '../config/tokenConfig';

const TGE_TARGETS = {
    users: 10000,
    predictions: 100000
};
const TGE_PROGRESS_URL = 'https://trendphile.com/api/tge-progress';

const TokenClaim = ({ walletAddress, userToken, userId }) => {
    const navigate = useNavigate();
    
    const [claimableAmounts, setClaimableAmounts] = useState({
        instant: '0',
        vested: '0'
    });
    const [walletBalance, setWalletBalance] = useState('0');
    const [databaseBalance, setDatabaseBalance] = useState('0'); // 🆕 Database balance (pending distribution)
    const [totalClaimed, setTotalClaimed] = useState('0'); // 🆕 Total already claimed
    const [tgeActivated, setTgeActivated] = useState(false);
    const [tgeProgress, setTgeProgress] = useState({
        users: null,
        predictions: null
    }); // Optional progress stats for TGE conditions
    const [tgeProgressLoading, setTgeProgressLoading] = useState(false);
    const [tgeProgressError, setTgeProgressError] = useState('');
    const [tgeProgressFetchedAt, setTgeProgressFetchedAt] = useState(0);
    const [tgeLastUpdated, setTgeLastUpdated] = useState(null);
    const [tgeProgressHasData, setTgeProgressHasData] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isCheckingTGE, setIsCheckingTGE] = useState(false);
    const [isClaiming, setIsClaiming] = useState(false);
    const [isDistributing, setIsDistributing] = useState(false); // 🆕 Distribution in progress
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [txHash, setTxHash] = useState(null);
    const [claimTxHash, setClaimTxHash] = useState(null); // 🆕 Claim transaction hash
    const [claimStatus, setClaimStatus] = useState(''); // 🆕 Claim status message
    const [isWrongNetwork, setIsWrongNetwork] = useState(false);
    const [currentChainId, setCurrentChainId] = useState(null);
    const [isSwitchingNetwork, setIsSwitchingNetwork] = useState(false);
    const [showTokenAddedConfirmation, setShowTokenAddedConfirmation] = useState(false); // Track if Add to MetaMask was clicked
    
    // 🆕 Two-Way Swap Modal State
    const [showSwapModal, setShowSwapModal] = useState(false);
    const [activeSwapTab, setActiveSwapTab] = useState('sell'); // 'sell' or 'buy'
    const [swapAmount, setSwapAmount] = useState('');
    const [swapOutputEstimate, setSwapOutputEstimate] = useState('0');
    const [swapPriceImpact, setSwapPriceImpact] = useState('0');
    const [isSwapping, setIsSwapping] = useState(false);
    const [swapStep, setSwapStep] = useState('');
    const [swapError, setSwapError] = useState('');
    const [swapSuccess, setSwapSuccess] = useState('');
    const [swapTxHash, setSwapTxHash] = useState(null);
    const [usdcBalance, setUsdcBalance] = useState('0');

    // Contract addresses from environment variables (support both VITE_ and REACT_APP_ prefixes)
    const USER_VESTING_CONTRACT = process.env.REACT_APP_USER_VESTING_CONTRACT || process.env.VITE_USER_VESTING_CONTRACT;
    const TPHILE_TOKEN_CONTRACT = process.env.REACT_APP_TPHILE_TOKEN_CONTRACT || process.env.VITE_TPHILE_TOKEN_CONTRACT;
    const TARGET_CHAIN_ID = 137; // Polygon Mainnet - hardcoded for reliability
    const TARGET_CHAIN_ID_HEX = '0x89'; // Hex format for MetaMask (137 in hex)
    const RPC_URL = process.env.REACT_APP_RPC_URL || process.env.VITE_RPC_URL || 'https://polygon-rpc.com';
    const EXPLORER_URL = process.env.REACT_APP_EXPLORER_URL || process.env.VITE_EXPLORER_URL || 'https://polygonscan.com';
    
    // 🆕 DEX Contract Addresses for Two-Way Swap feature
    const USDC_TOKEN = '0x8B0180f2101c8260d49339abfEe87927412494B4';
    const UNISWAP_ROUTER = '0x7D4632201805A48FeFD6C2228fEE0828725f309D';
    const PAIR_ADDRESS = '0xAE8731ae02b17AEbcCbf0c405bdD21E736f7059E';
    
    // 🆕 Fetch USDC balance
    const fetchUSDCBalance = async () => {
        if (!walletAddress || !USDC_TOKEN) return;
        
        try {
            await checkNetwork();
            const provider = getProvider();
            const usdcContract = new ethers.Contract(
                USDC_TOKEN,
                ['function balanceOf(address) view returns (uint256)'],
                provider
            );
            
            const balance = await usdcContract.balanceOf(walletAddress);
            const formattedBalance = ethers.formatUnits(balance, 6); // USDC has 6 decimals
            console.log('💵 USDC Balance:', formattedBalance);
            setUsdcBalance(formattedBalance);
        } catch (err) {
            console.error('Error fetching USDC balance:', err);
        }
    };

    // 🔧 Mobile detection - Enhanced for MetaMask browser
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const hasEthereumProvider = typeof window.ethereum !== 'undefined';
    
    // Multiple checks for MetaMask browser detection
    const isMetaMaskBrowser = 
        // Check 1: Standard MetaMask flag
        window.ethereum?.isMetaMask ||
        // Check 2: MetaMask in user agent (mobile specific)
        /MetaMask/i.test(navigator.userAgent) ||
        // Check 3: On mobile with ethereum provider = likely MetaMask browser
        (isMobile && hasEthereumProvider);
    
    // State for provider readiness
    const [providerReady, setProviderReady] = useState(hasEthereumProvider);

    // 🔧 Wait for ethereum provider to be ready (mobile can be slow)
    useEffect(() => {
        if (window.ethereum) {
            setProviderReady(true);
            console.log('✅ Ethereum provider ready');
        } else {
            // Keep checking for provider (mobile can be slow to inject)
            const checkProvider = setInterval(() => {
                if (window.ethereum) {
                    console.log('✅ Provider detected after delay:', window.ethereum);
                    setProviderReady(true);
                    clearInterval(checkProvider);
                }
            }, 200);

            // Stop checking after 3 seconds
            const timeout = setTimeout(() => {
                clearInterval(checkProvider);
                if (!window.ethereum) {
                    console.log('⚠️ No provider found after 3 seconds');
                }
            }, 3000);

            return () => {
                clearInterval(checkProvider);
                clearTimeout(timeout);
            };
        }
    }, []);

    // 🔧 Network change and account change listeners
    useEffect(() => {
        if (window.ethereum) {
            const handleChainChanged = (chainIdHex) => {
                console.log('🔗 Chain changed to:', chainIdHex, '(decimal:', parseInt(chainIdHex, 16), ')');
                const newChainId = parseInt(chainIdHex, 16);
                setCurrentChainId(newChainId);
                setIsWrongNetwork(newChainId !== TARGET_CHAIN_ID);
                
                // Re-fetch data when network changes to correct one
                if (newChainId === TARGET_CHAIN_ID) {
                    setError('');
                    fetchClaimableAmounts();
                    checkTGEStatus();
                    fetchWalletBalance();
                }
            };

            const handleAccountsChanged = (accounts) => {
                console.log('👛 Accounts changed:', accounts);
                if (accounts.length === 0) {
                    // User disconnected wallet
                    setError('Wallet disconnected');
                } else {
                    // Refresh data for new account
                    window.location.reload();
                }
            };

            window.ethereum.on('chainChanged', handleChainChanged);
            window.ethereum.on('accountsChanged', handleAccountsChanged);

            // Initial network check
            checkCurrentNetwork();

            return () => {
                window.ethereum.removeListener('chainChanged', handleChainChanged);
                window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
            };
        }
    }, []);

    // 🔧 Check current network on mount and when wallet connects
    const checkCurrentNetwork = useCallback(async () => {
        if (!window.ethereum) {
            console.log('❌ MetaMask not installed');
            return false;
        }

        try {
            // Get chain ID directly from MetaMask (most reliable method)
            const chainIdHex = await window.ethereum.request({ method: 'eth_chainId' });
            const detectedChainId = parseInt(chainIdHex, 16);
            
            console.log('🔍 Network Detection:');
            console.log('   Chain ID (hex):', chainIdHex);
            console.log('   Chain ID (decimal):', detectedChainId);
            console.log('   Target Chain ID:', TARGET_CHAIN_ID);
            console.log('   Match:', detectedChainId === TARGET_CHAIN_ID);
            
            setCurrentChainId(detectedChainId);
            const wrongNetwork = detectedChainId !== TARGET_CHAIN_ID;
            setIsWrongNetwork(wrongNetwork);
            
            return !wrongNetwork;
        } catch (err) {
            console.error('❌ Error checking network:', err);
            return false;
        }
    }, [TARGET_CHAIN_ID]);

    // 🔧 Open MetaMask app via deep link (for mobile)
    const openMetaMaskDeepLink = () => {
        const currentUrl = window.location.href;
        const dappUrl = currentUrl.replace(/^https?:\/\//, '');
        const metamaskDeepLink = `https://metamask.app.link/dapp/${dappUrl}`;
        console.log('📱 Opening MetaMask via deep link:', metamaskDeepLink);
        window.location.href = metamaskDeepLink;
    };

    // 🔧 Switch to Polygon Mainnet network
    const switchToPolygon = async () => {
        // On mobile without MetaMask provider, redirect to MetaMask app
        if (isMobile && !hasEthereumProvider) {
            openMetaMaskDeepLink();
            return;
        }

        if (!window.ethereum) {
            setError('MetaMask is not installed. Please install MetaMask to continue.');
            return;
        }

        setIsSwitchingNetwork(true);
        setError('');

        try {
            // Try to switch to Polygon Mainnet
            await window.ethereum.request({
                method: 'wallet_switchEthereumChain',
                params: [{ chainId: TARGET_CHAIN_ID_HEX }],
            });
            console.log('✅ Switched to Polygon Mainnet');
        } catch (switchError) {
            // If network doesn't exist, add it
            if (switchError.code === 4902) {
                try {
                    await window.ethereum.request({
                        method: 'wallet_addEthereumChain',
                        params: [{
                            chainId: TARGET_CHAIN_ID_HEX,
                            chainName: 'Polygon Mainnet',
                            nativeCurrency: {
                                name: 'POL',
                                symbol: 'POL',
                                decimals: 18
                            },
                            rpcUrls: ['https://polygon-rpc.com'],
                            blockExplorerUrls: ['https://polygonscan.com']
                        }]
                    });
                    console.log('✅ Added and switched to Polygon Mainnet');
                } catch (addError) {
                    console.error('❌ Error adding network:', addError);
                    setError('Failed to add Polygon Mainnet network. Please add it manually in MetaMask.');
                }
            } else {
                console.error('❌ Error switching network:', switchError);
                setError('Failed to switch network. Please switch manually in MetaMask.');
            }
        } finally {
            setIsSwitchingNetwork(false);
        }
    };

    useEffect(() => {
        // Backend-only TGE status
        checkTGEStatus();
        
        if (walletAddress && !isWrongNetwork) {
            fetchClaimableAmounts();
            fetchWalletBalance();
            fetchDatabaseBalance(); // 🆕 Fetch database balance
            fetchUSDCBalance(); // 🆕 Fetch USDC balance
        } else if (!walletAddress) {
            setIsLoading(false);
        }
    }, [walletAddress, isWrongNetwork]);

    // 🆕 Close modal on ESC key
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && showSwapModal && !isSwapping) {
                setShowSwapModal(false);
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [showSwapModal, isSwapping]);
    
    // 🆕 Lock body scroll when modal open
    useEffect(() => {
        if (showSwapModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [showSwapModal]);

    // 🆕 Fetch database balance (pending distribution)
    const fetchDatabaseBalance = async () => {
        if (!userToken || !userId) {
            console.log('⚠️ Cannot fetch database balance: missing userToken or userId');
            return;
        }

        try {
            const API_URL = process.env.REACT_APP_API_URL || '';
            const response = await fetch(`${API_URL}/ftap/balance?userId=${userId}`, {
                headers: {
                    'Authorization': `Bearer ${userToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                console.error('❌ Failed to fetch database balance:', response.status);
                return;
            }

            const data = await response.json();
            console.log('📊 Database Balance Response:', data);
            
            const balanceData = data.data || data;
            const activeBalance = balanceData.active_balance || balanceData.total_allocated || '0';
            console.log('💰 Database Active Balance:', activeBalance);
            setDatabaseBalance(activeBalance.toString());

            // Also fetch claim history to calculate already claimed
            await fetchClaimHistory();
        } catch (err) {
            console.error('❌ Error fetching database balance:', err);
        }
    };

    // 🆕 Fetch claim history to calculate total claimed
    const fetchClaimHistory = async () => {
        if (!userToken) return;

        try {
            const API_URL = process.env.REACT_APP_API_URL || '';
            const response = await fetch(`${API_URL}/claim-history`, {
                headers: {
                    'Authorization': `Bearer ${userToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) return;

            const data = await response.json();
            if (data.success && data.claims) {
                const confirmed = data.claims
                    .filter(c => c.status === 'confirmed')
                    .reduce((sum, c) => sum + c.amount, 0);
                setTotalClaimed(confirmed.toString());
                console.log('📊 Total Claimed:', confirmed);
            }
        } catch (err) {
            console.error('❌ Error fetching claim history:', err);
        }
    };

    // 🆕 Handle claim/distribute button click
    const handleDistribute = async () => {
        if (!walletAddress) {
            setClaimStatus('❌ Please connect your wallet first');
            return;
        }

        const pendingAmount = parseFloat(databaseBalance) - parseFloat(totalClaimed);
        if (pendingAmount <= 0) {
            setClaimStatus('❌ No tokens available to distribute');
            return;
        }

        setIsDistributing(true);
        setClaimStatus('🚀 Initiating distribution...');
        setClaimTxHash(null);
        setError('');
        setSuccessMessage('');

        try {
            const API_URL = process.env.REACT_APP_API_URL || '';
            const response = await fetch(`${API_URL}/claim-tokens`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${userToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ walletAddress })
            });

            const data = await response.json();
            console.log('📊 Claim Response:', data);

            if (!data.success) {
                setClaimStatus(`❌ ${data.message}`);
                setIsDistributing(false);
                return;
            }

            setClaimTxHash(data.txHash);
            setClaimStatus(`🚀 Distribution initiated! TX: ${data.txHash.slice(0, 10)}...`);

            // Poll for confirmation
            const claimId = data.claimId;
            let pollCount = 0;
            const maxPolls = 60; // 3 minutes max (60 * 3 seconds)

            const pollInterval = setInterval(async () => {
                pollCount++;
                
                try {
                    const statusResponse = await fetch(`${API_URL}/claim-status/${claimId}`, {
                        headers: {
                            'Authorization': `Bearer ${userToken}`,
                            'Content-Type': 'application/json'
                        }
                    });

                    const statusData = await statusResponse.json();
                    
                    if (statusData.success && statusData.claim) {
                        const claim = statusData.claim;
                        
                        if (claim.status === 'confirmed') {
                            clearInterval(pollInterval);
                            setClaimStatus(`✅ Successfully distributed ${data.amount.toLocaleString()} TPHILE to your wallet!`);
                            setSuccessMessage(`${data.amount.toLocaleString()} TPHILE sent to your wallet!`);
                            setIsDistributing(false);
                            
                            // Refresh balances after short delay
                            setTimeout(() => {
                                fetchDatabaseBalance();
                                fetchWalletBalance();
                            }, 2000);
                        } else if (claim.status === 'failed') {
                            clearInterval(pollInterval);
                            setClaimStatus(`❌ Distribution failed: ${claim.errorMessage || 'Unknown error'}`);
                            setError(claim.errorMessage || 'Distribution failed');
                            setIsDistributing(false);
                        }
                    }
                } catch (pollErr) {
                    console.error('Poll error:', pollErr);
                }

                // Stop polling after max attempts
                if (pollCount >= maxPolls) {
                    clearInterval(pollInterval);
                    setClaimStatus('⏱️ Distribution taking longer than expected. Check your wallet or refresh later.');
                    setIsDistributing(false);
                }
            }, 3000); // Poll every 3 seconds

        } catch (err) {
            console.error('❌ Claim error:', err);
            setClaimStatus(`❌ Failed to initiate distribution: ${err.message}`);
            setIsDistributing(false);
        }
    };

    const getProvider = () => {
        if (!window.ethereum) {
            // On mobile, don't throw error - redirect to MetaMask instead
            if (isMobile) {
                openMetaMaskDeepLink();
                return null;
            }
            throw new Error('MetaMask is not installed. Please install MetaMask to continue.');
        }
        return new ethers.BrowserProvider(window.ethereum);
    };

    // 🔧 Improved network check - uses direct comparison with proper type handling
    const checkNetwork = async () => {
        if (!window.ethereum) {
            // On mobile without provider, don't throw - let UI handle it
            if (isMobile) {
                return false;
            }
            throw new Error('MetaMask is not installed. Please install MetaMask to continue.');
        }

        // Get chain ID directly from MetaMask
        const chainIdHex = await window.ethereum.request({ method: 'eth_chainId' });
        const detectedChainId = parseInt(chainIdHex, 16);
        
        console.log('🔍 checkNetwork():');
        console.log('   Detected Chain ID:', detectedChainId, '(type:', typeof detectedChainId, ')');
        console.log('   Target Chain ID:', TARGET_CHAIN_ID, '(type:', typeof TARGET_CHAIN_ID, ')');
        
        if (detectedChainId !== TARGET_CHAIN_ID) {
            setIsWrongNetwork(true);
            setCurrentChainId(detectedChainId);
            throw new Error(`Wrong network detected. Please switch to Polygon Mainnet.`);
        }
        
        setIsWrongNetwork(false);
        setCurrentChainId(detectedChainId);
    };

    const fetchClaimableAmounts = async () => {
        if (!walletAddress) return;

        try {
            setIsLoading(true);
            setError('');

            await checkNetwork();
            const provider = getProvider();
            const contract = new ethers.Contract(
                USER_VESTING_CONTRACT,
                UserVestingABI.abi,
                provider
            );

            // Call getUserClaimable(address) - returns [instantClaimable, vestedClaimable]
            const result = await contract.getUserClaimable(walletAddress);
            
            setClaimableAmounts({
                instant: ethers.formatEther(result[0]),
                vested: ethers.formatEther(result[1])
            });

        } catch (err) {
            console.error('Error fetching claimable amounts:', err);
            setError(err.message || 'Failed to fetch claimable amounts');
        } finally {
            setIsLoading(false);
        }
    };

    // 📊 Fetch TGE progress (registered users & predictions)
    const fetchTGEProgress = useCallback(async ({ force = false } = {}) => {
        const progressUrl = TGE_PROGRESS_URL;
        const now = Date.now();

        const cacheValid = tgeProgressFetchedAt && (now - tgeProgressFetchedAt) < 30000;
        const hasData = tgeProgress.users !== null && tgeProgress.predictions !== null;

        if (!force && cacheValid && hasData) {
            return;
        }

        setTgeProgressLoading(true);
        setTgeProgressError('');

        let attempt = 0;
        let delay = 500;

        while (attempt < 3) {
            try {
                const response = await fetch(progressUrl, {
                    headers: { 'Content-Type': 'application/json' }
                });

                const status = response.status;
                const text = await response.text();
                console.log('[TGE] fetch status', status, 'url', progressUrl, 'rawText', text);
                if (!response.ok) {
                    console.error('TGE progress fetch failed', { status, progressUrl, text });
                    throw new Error('Unable to fetch progress data');
                }

                let data;
                try {
                    data = JSON.parse(text);
                } catch (parseErr) {
                    console.error('TGE progress JSON parse error', parseErr, { text });
                    throw new Error('Unable to fetch progress data');
                }
                console.log('[TGE] parsed data', data);

                if (
                    typeof data !== 'object' ||
                    data === null ||
                    (data.registeredUsers === undefined && data.users === undefined) ||
                    (data.totalPredictions === undefined && data.predictions === undefined)
                ) {
                    console.error('TGE progress payload invalid shape', data);
                    throw new Error('Unable to fetch progress data');
                }

                const usersCount = parseInt(data.registeredUsers ?? data.users ?? 0, 10) || 0;
                const predictionsCount = parseInt(data.totalPredictions ?? data.predictions ?? 0, 10) || 0;
                const apiTgeActivated = Boolean(data.tgeActivated ?? false);

                setTgeProgress({
                    users: usersCount,
                    predictions: predictionsCount
                });
                setTgeActivated(apiTgeActivated);
                setTgeProgressHasData(true);

                const fetchedAt = Date.now();
                setTgeProgressFetchedAt(fetchedAt);
                setTgeLastUpdated(new Date(fetchedAt).toISOString());
                setTgeProgressLoading(false);
                return;
            } catch (err) {
                attempt += 1;
                if (attempt >= 3) {
                    setTgeProgressError('Unable to fetch progress data');
                    setTgeProgressLoading(false);
                    return;
                }
                await new Promise(resolve => setTimeout(resolve, delay));
                delay *= 2;
            }
        }
    }, [tgeProgressFetchedAt, tgeProgress.users, tgeProgress.predictions]);

    const checkTGEStatus = async () => {
        // Backend-driven TGE status only
        setIsCheckingTGE(true);
        try {
            await fetchTGEProgress({ force: true });
        } finally {
            setIsCheckingTGE(false);
        }
    };

    // 📊 TGE progress polling (initial + every 30s)
    useEffect(() => {
        fetchTGEProgress({ force: true });
        const interval = setInterval(() => {
            fetchTGEProgress({ force: true });
        }, 30000);
        return () => clearInterval(interval);
    }, [fetchTGEProgress]);

    // 🆕 Fetch actual wallet token balance
    const fetchWalletBalance = async () => {
        if (!walletAddress || !TPHILE_TOKEN_CONTRACT) return;

        try {
            await checkNetwork();
            const provider = getProvider();
            const tokenContract = new ethers.Contract(
                TPHILE_TOKEN_CONTRACT,
                TPhileTokenABI.abi,
                provider
            );

            const balance = await tokenContract.balanceOf(walletAddress);
            const formattedBalance = ethers.formatEther(balance);
            console.log('💰 Wallet Token Balance:', formattedBalance, 'TPHILE');
            setWalletBalance(formattedBalance);

        } catch (err) {
            console.error('Error fetching wallet balance:', err);
        }
    };

    const claimInstant = async () => {
        if (!walletAddress) {
            setError('Wallet not connected');
            return;
        }

        if (parseFloat(claimableAmounts.instant) === 0) {
            setError('No instant tokens available to claim');
            return;
        }

        try {
            setIsClaiming(true);
            setError('');
            setSuccessMessage('');

            await checkNetwork();
            const provider = getProvider();
            const signer = await provider.getSigner();
            const contract = new ethers.Contract(
                USER_VESTING_CONTRACT,
                UserVestingABI.abi,
                signer
            );

            // Call claimInstantReward() - no parameters
            const tx = await contract.claimInstantReward();
            setTxHash(tx.hash);
            setSuccessMessage('Transaction submitted! Waiting for confirmation...');

            // Wait for confirmation
            const receipt = await tx.wait();
            
            setSuccessMessage(`Successfully claimed ${claimableAmounts.instant} TPHILE tokens!`);
            
            // Refresh claimable amounts
            await fetchClaimableAmounts();

        } catch (err) {
            console.error('Error claiming instant tokens:', err);
            setError(err.message || 'Failed to claim instant tokens');
            setTxHash(null);
        } finally {
            setIsClaiming(false);
        }
    };

    const claimVested = async () => {
        if (!walletAddress) {
            setError('Wallet not connected');
            return;
        }

        if (parseFloat(claimableAmounts.vested) === 0) {
            setError('No vested tokens available to claim');
            return;
        }

        try {
            setIsClaiming(true);
            setError('');
            setSuccessMessage('');

            await checkNetwork();
            const provider = getProvider();
            const signer = await provider.getSigner();
            const contract = new ethers.Contract(
                USER_VESTING_CONTRACT,
                UserVestingABI.abi,
                signer
            );

            // Call claimVestedReward() - no parameters
            const tx = await contract.claimVestedReward();
            setTxHash(tx.hash);
            setSuccessMessage('Transaction submitted! Waiting for confirmation...');

            // Wait for confirmation
            const receipt = await tx.wait();
            
            setSuccessMessage(`Successfully claimed ${claimableAmounts.vested} TPHILE tokens!`);
            
            // Refresh claimable amounts
            await fetchClaimableAmounts();

        } catch (err) {
            console.error('Error claiming vested tokens:', err);
            setError(err.message || 'Failed to claim vested tokens');
            setTxHash(null);
        } finally {
            setIsClaiming(false);
        }
    };

    // 🌙 Add TPH token to MetaMask wallet
    const addTokenToWallet = async () => {
        // Show confirmation message when button is clicked
        setShowTokenAddedConfirmation(true);
        
        // Use checksummed address from config
        const tokenAddress = ethers.getAddress(
            TPHILE_TOKEN_CONTRACT || ACTIVE_NETWORK.tokenAddress || '0x9A77152343981490c6477aF75A7c3FCA28cf9f00'
        );
        const tokenSymbol = TOKEN_METADATA.symbol;
        const tokenDecimals = TOKEN_METADATA.decimals;
        const tokenImage = TOKEN_METADATA.image;

        // 🔍 Enhanced detection with debugging
        const currentIsMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        const currentHasProvider = typeof window.ethereum !== 'undefined';
        const currentIsMetaMask = window.ethereum?.isMetaMask;
        const userAgentHasMetaMask = /MetaMask/i.test(navigator.userAgent);
        
        // More robust MetaMask browser detection
        const inMetaMaskBrowser = currentIsMetaMask || userAgentHasMetaMask || (currentIsMobile && currentHasProvider);
        
        console.log('===== MetaMask Detection Debug =====');
        console.log('User Agent:', navigator.userAgent);
        console.log('Is Mobile:', currentIsMobile);
        console.log('Has Provider:', currentHasProvider);
        console.log('isMetaMask flag:', currentIsMetaMask);
        console.log('MetaMask in UA:', userAgentHasMetaMask);
        console.log('In MetaMask Browser:', inMetaMaskBrowser);
        console.log('Provider Ready State:', providerReady);
        console.log('=====================================');

        // If provider not ready yet, wait a moment
        if (!currentHasProvider && currentIsMobile) {
            console.log('⏳ Provider not ready, waiting...');
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Check again after waiting
            if (window.ethereum) {
                console.log('✅ Provider now available');
            }
        }

        // Re-check after potential wait
        const finalHasProvider = typeof window.ethereum !== 'undefined';
        const finalInMetaMaskBrowser = window.ethereum?.isMetaMask || userAgentHasMetaMask || (currentIsMobile && finalHasProvider);

        // Check if we're on mobile but definitely NOT in MetaMask browser
        if (currentIsMobile && !finalInMetaMaskBrowser && !finalHasProvider) {
            console.log('📱 Mobile detected, no provider - redirecting to MetaMask app');
            
            // Store token params for retry after redirect
            const tokenParams = {
                type: 'ERC20',
                options: {
                    address: tokenAddress,
                    symbol: tokenSymbol,
                    decimals: tokenDecimals,
                    image: tokenImage
                }
            };
            sessionStorage.setItem('pendingTokenAdd', JSON.stringify(tokenParams));
            
            // Show helpful message and redirect
            setSuccessMessage('Opening MetaMask app... After connecting, tap "Add to MetaMask" again.');
            setTimeout(() => {
                openMetaMaskDeepLink();
            }, 1500);
            return;
        }

        // If we have a provider, try to add the token
        if (window.ethereum) {
            try {
                // 🔍 Step 1: Check current network
                console.log('🔍 Checking network...');
                const currentNetwork = await getCurrentNetwork();
                console.log('Current Network:', currentNetwork);
                
                // 🔍 Step 2: Verify we're on the correct network
                if (currentNetwork && currentNetwork.chainId !== ACTIVE_NETWORK.chainId) {
                    console.log(`⚠️ Wrong network! Expected ${ACTIVE_NETWORK.name} (${ACTIVE_NETWORK.chainId}), got ${currentNetwork.name} (${currentNetwork.chainId})`);
                    
                    // Prompt user to switch network
                    setError(`Please switch to ${ACTIVE_NETWORK.name} to add this token.`);
                    
                    try {
                        console.log('🔄 Attempting to switch network...');
                        await switchToNetwork(ACTIVE_NETWORK);
                        console.log('✅ Network switched successfully!');
                        setError('');
                        // Wait a moment for network to stabilize
                        await new Promise(resolve => setTimeout(resolve, 1000));
                    } catch (switchErr) {
                        console.error('❌ Failed to switch network:', switchErr);
                        setError(`Please switch to ${ACTIVE_NETWORK.name} in MetaMask to add this token.`);
                        return;
                    }
                }

                // 🔍 Step 3: Verify token contract exists on this network
                console.log('🔍 Verifying token contract...');
                try {
                    const provider = new ethers.BrowserProvider(window.ethereum);
                    const code = await provider.getCode(tokenAddress);
                    
                    if (code === '0x' || code === '0x0') {
                        console.error('❌ Token contract not found at address:', tokenAddress);
                        setError(`Token contract not found on ${ACTIVE_NETWORK.name}. Please ensure you're on the correct network.`);
                        return;
                    }
                    console.log('✅ Token contract verified');
                } catch (verifyErr) {
                    console.error('❌ Error verifying contract:', verifyErr);
                    // Continue anyway - might be a network issue
                }

                // 🔍 Step 4: Add the token
                console.log('🌙 Adding TPH token to MetaMask...');
                console.log('   Token Address:', tokenAddress);
                console.log('   Symbol:', tokenSymbol);
                console.log('   Network:', ACTIVE_NETWORK.name);
                
                const wasAdded = await window.ethereum.request({
                    method: 'wallet_watchAsset',
                    params: {
                        type: 'ERC20',
                        options: {
                            address: tokenAddress,
                            symbol: tokenSymbol,
                            decimals: tokenDecimals,
                            image: tokenImage,
                        },
                    },
                });

                if (wasAdded) {
                    console.log('✅ TPH token added to MetaMask successfully!');
                    setSuccessMessage('TPH token added to MetaMask! 🎉');
                    setError('');
                    // Clear any pending token add
                    sessionStorage.removeItem('pendingTokenAdd');
                } else {
                    console.log('ℹ️ User declined to add TPH token');
                }
            } catch (err) {
                console.error('❌ Error adding TPH token to MetaMask:', err);
                console.error('Error details:', {
                    message: err.message,
                    code: err.code,
                    data: err.data
                });
                
                if (err.code === 4001) {
                    // User rejected the request
                    setError('Token addition cancelled');
                } else if (err.code === -32002) {
                    setError('Please check MetaMask - a request is pending');
                } else if (err.message?.includes('not supported on this network')) {
                    // Network mismatch error
                    setError(`This token is only available on ${ACTIVE_NETWORK.name}. Please switch networks.`);
                    // Try to switch network
                    try {
                        await switchToNetwork(ACTIVE_NETWORK);
                        setSuccessMessage(`Switched to ${ACTIVE_NETWORK.name}. Please try adding the token again.`);
                        setError('');
                    } catch (switchErr) {
                        console.error('Failed to auto-switch:', switchErr);
                    }
                } else {
                    setError(`Failed to add token: ${err.message || 'Unknown error'}. Make sure you're on ${ACTIVE_NETWORK.name}.`);
                }
            }
        } else {
            // No provider found at all
            console.error('❌ No ethereum provider found');
            if (currentIsMobile) {
                // On mobile without provider, redirect to MetaMask
                setSuccessMessage('Opening MetaMask app...');
                setTimeout(() => {
                    openMetaMaskDeepLink();
                }, 500);
            } else {
                setError('MetaMask not detected. Please install MetaMask extension.');
            }
        }
    };

    // 🔄 Check for pending token add after returning from MetaMask app
    useEffect(() => {
        const checkPendingTokenAdd = async () => {
            const pendingTokenAdd = sessionStorage.getItem('pendingTokenAdd');
            
            if (pendingTokenAdd && window.ethereum?.isMetaMask) {
                console.log('🔄 Found pending token add, attempting...');
                const tokenParams = JSON.parse(pendingTokenAdd);
                
                // Wait for provider to be ready
                setTimeout(async () => {
                    try {
                        const wasAdded = await window.ethereum.request({
                            method: 'wallet_watchAsset',
                            params: tokenParams
                        });
                        
                        if (wasAdded) {
                            console.log('✅ Token added after redirect!');
                            setSuccessMessage('TPH token added to MetaMask! 🎉');
                        }
                        sessionStorage.removeItem('pendingTokenAdd');
                    } catch (err) {
                        console.error('❌ Failed to add token after redirect:', err);
                        sessionStorage.removeItem('pendingTokenAdd');
                    }
                }, 1500);
            }
        };
        
        checkPendingTokenAdd();
    }, []);

    // 🆕 Calculate swap output estimate (supports both directions)
    const calculateSwapOutput = useCallback(async (amount, direction) => {
        if (!amount || parseFloat(amount) <= 0) {
            setSwapOutputEstimate('0');
            setSwapPriceImpact('0');
            return;
        }

        try {
            const fromToken = direction === 'sell' ? 'TPH' : 'USDC';
            
            const response = await fetch('https://dex.trendphile.com/api/swap/estimate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    amountIn: amount, 
                    fromToken: fromToken
                })
            });
            
            const data = await response.json();
            
            if (data.success) {
                setSwapOutputEstimate(data.amountOut);
                setSwapPriceImpact(data.priceImpact);
            }
        } catch (error) {
            console.error('Error calculating swap:', error);
        }
    }, []);

    // 🆕 Handle swap amount change with direction awareness
    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            if (swapAmount) {
                calculateSwapOutput(swapAmount, activeSwapTab);
            }
        }, 500);
        
        return () => clearTimeout(debounceTimer);
    }, [swapAmount, activeSwapTab, calculateSwapOutput]);
    
    // 🆕 Reset amount when switching tabs
    useEffect(() => {
        setSwapAmount('');
        setSwapOutputEstimate('0');
        setSwapPriceImpact('0');
        setSwapError('');
        setSwapSuccess('');
    }, [activeSwapTab]);

    // 🆕 Execute swap (handles both SELL and BUY directions)
    const executeSwap = async () => {
        console.log('═══════════ SWAP EXECUTION STARTED ═══════════');
        console.log('Direction:', activeSwapTab.toUpperCase());
        console.log('Wallet Address:', walletAddress);
        console.log('Swap Amount:', swapAmount);
        console.log('TPH Balance:', walletBalance);
        console.log('USDC Balance:', usdcBalance);
        
        if (!walletAddress || !swapAmount || parseFloat(swapAmount) <= 0) {
            console.log('❌ Validation failed: Invalid amount');
            setSwapError('Please enter a valid amount');
            return;
        }

        // Validate balance based on direction
        const availableBalance = activeSwapTab === 'sell' ? walletBalance : usdcBalance;
        const tokenName = activeSwapTab === 'sell' ? 'TPH' : 'USDC';
        
        if (parseFloat(swapAmount) > parseFloat(availableBalance)) {
            console.log(`❌ Validation failed: Insufficient ${tokenName} balance`);
            setSwapError(`Insufficient ${tokenName} balance`);
            return;
        }

        setIsSwapping(true);
        setSwapError('');
        setSwapSuccess('');
        setSwapTxHash(null);

        try {
            console.log('🔍 Step 1: Verifying network...');
            await checkNetwork();
            console.log('✅ Network verified');

            // SIMPLIFIED: Call backend API to handle swap
            const apiUrl = 'https://dex.trendphile.com/api/swap/execute';
            console.log('🔍 Step 2: Calling API:', apiUrl);
            console.log('Request body:', { amount: swapAmount, walletAddress });

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    amount: swapAmount,
                    walletAddress: walletAddress,
                    fromToken: activeSwapTab === 'sell' ? 'TPH' : 'USDC',
                    toToken: activeSwapTab === 'sell' ? 'USDC' : 'TPH'
                }),
                signal: controller.signal,
                mode: 'cors'
            });

            clearTimeout(timeoutId);

            console.log('📡 Response received');
            console.log('Status:', response.status);
            console.log('Status Text:', response.statusText);
            console.log('Headers:', Object.fromEntries(response.headers.entries()));

            if (!response.ok) {
                console.log('❌ Response not OK');
                const errorText = await response.text();
                console.log('Error response:', errorText);
                throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
            }

            const data = await response.json();
            console.log('✅ Response data:', data);

            if (!data.success) {
                throw new Error(data.error || data.message || 'Swap failed');
            }

            // Success!
            console.log('✅ Swap successful!');
            setSwapTxHash(data.txHash);
            setSwapSuccess(`Successfully swapped ${parseFloat(swapAmount).toLocaleString()} TPH for ${data.estimatedUSDC} USDC!`);
            setSwapAmount('');
            setSwapOutputEstimate('0');
            
            // Refresh wallet balance after short delay
            setTimeout(() => {
                fetchWalletBalance();
                setShowSwapModal(false);
            }, 3000);

        } catch (error) {
            console.error('═══════════ SWAP ERROR ═══════════');
            console.error('Error name:', error.name);
            console.error('Error message:', error.message);
            console.error('Full error:', error);
            console.error('═══════════════════════════════════');
            
            let userMessage;
            if (error.name === 'AbortError') {
                userMessage = 'Request timed out after 30 seconds. Please check your internet connection.';
            } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
                userMessage = 'Network error: Cannot reach swap service. Please check if you\'re online and try again.';
            } else if (error.message.includes('HTTP 404')) {
                userMessage = 'Swap endpoint not found. The service may be updating. Please try again in a moment.';
            } else if (error.message.includes('HTTP 500')) {
                userMessage = 'Server error. Our team has been notified. Please try again later.';
            } else {
                userMessage = error.message || 'Swap failed. Please try again or contact support.';
            }
            
            setSwapError(userMessage);
        } finally {
            setIsSwapping(false);
        }
    };

    // 🆕 Quick amount buttons (25%, 50%, 75%, 100%) - direction aware
    const setQuickAmount = (percentage) => {
        const balance = activeSwapTab === 'sell' ? parseFloat(walletBalance) : parseFloat(usdcBalance);
        const decimals = activeSwapTab === 'sell' ? 0 : 2; // TPH = whole numbers, USDC = 2 decimals
        const amount = (balance * percentage / 100).toFixed(decimals);
        setSwapAmount(amount);
    };

    if (!walletAddress) {
        return (
            <div className="token-claim-container">
                <div className="token-claim-card">
                    <div className="claim-header">
                        <Coins size={24} />
                        <h3>Claim Tokens</h3>
                    </div>
                    <div className="claim-message">
                        <AlertCircle size={20} />
                        <p>Please connect your wallet to claim tokens</p>
                    </div>
                </div>
            </div>
        );
    }

    // 🔧 Show network switch UI when on wrong network
    if (isWrongNetwork) {
        return (
            <div className="token-claim-container">
                <div className="token-claim-card">
                    <div className="claim-header">
                        <AlertCircle size={24} />
                        <h3>Wrong Network</h3>
                    </div>
                    <div className="network-error-message">
                        <Shield size={48} className="network-icon error" />
                        <h4>Please Switch Networks</h4>
                        <p>
                            You are currently on Chain ID: <strong>{currentChainId}</strong>
                        </p>
                        <p>
                            Please switch to <strong>Polygon Mainnet</strong> (Chain ID: {TARGET_CHAIN_ID})
                        </p>
                        <button 
                            className="switch-network-btn"
                            onClick={switchToPolygon}
                            disabled={isSwitchingNetwork}
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '12px',
                                width: '100%'
                            }}
                        >
                            {isSwitchingNetwork ? (
                                <>
                                    <Loader className="spinner" size={18} />
                                    <span style={{ flexShrink: 0 }}>Switching...</span>
                                </>
                            ) : (
                                <>
                                    <RefreshCw size={18} />
                                    <span style={{ flexShrink: 0 }}>Switch to Polygon Mainnet</span>
                                </>
                            )}
                        </button>
                        <p className="network-help-text">
                            Or switch manually in MetaMask
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (!tgeActivated) {
        // TGE check completed but TGE is not activated
        const userProgress = tgeProgress.users !== null ? Math.min(100, (tgeProgress.users / TGE_TARGETS.users) * 100) : 0;
        const predictionProgress = tgeProgress.predictions !== null ? Math.min(100, (tgeProgress.predictions / TGE_TARGETS.predictions) * 100) : 0;
        const lastUpdatedLabel = tgeLastUpdated ? new Date(tgeLastUpdated).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : null;

        const formatCount = (value, target) => {
            if (!tgeProgressHasData && tgeProgressLoading) {
                return 'Loading progress...';
            }
            if (tgeProgressError) {
                return 'Unable to fetch progress data';
            }
            if (value === null || value === undefined) {
                return `Awaiting data • Target: ${target.toLocaleString()}`;
            }
            return `${Number(value).toLocaleString()} / ${target.toLocaleString()}`;
        };

        return (
            <div className="token-claim-container">
                <div className="token-claim-card">
                    <div className="claim-header">
                        <Clock size={24} />
                        <h3>Token Claim</h3>
                    </div>
                    <div className="tge-inactive-message enhanced">
                        <div className="tge-title-row">
                            <div className="tge-icon-wrap">
                                <Clock size={20} />
                            </div>
                            <div className="tge-title-text">
                                <h4>TGE Not Activated Yet</h4>
                                <p className="tge-subtext">We’re preparing for the Token Generation Event.</p>
                            </div>
                            <div className="tge-icon-wrap right">
                                <Shield size={20} />
                            </div>
                        </div>

                        <div className="tge-explainer">
                            <div className="explainer-heading">What is TGE?</div>
                            <p>TGE (Token Generation Event) is the moment when TPHILE tokens become available for you to claim and use.</p>
                        </div>

                        <div className="tge-conditions">
                            <div className="conditions-heading">Activation conditions</div>
                            <div className="tge-progress-meta">
                                {(!tgeProgressHasData && tgeProgressLoading) && <Loader className="spinning" size={14} />}
                                {tgeProgressError && <AlertCircle size={14} className="tge-progress-error-icon" />}
                                <span className={tgeProgressError ? 'tge-progress-error' : ''}>
                                    {(!tgeProgressHasData && tgeProgressLoading)
                                        ? 'Loading progress...'
                                        : tgeProgressError
                                            ? 'Unable to fetch progress data'
                                            : lastUpdatedLabel
                                                ? `Last updated: ${lastUpdatedLabel}`
                                                : 'Awaiting data'}
                                </span>
                            </div>
                            <div className="conditions-grid">
                                <div className="condition-card">
                                    <div className="condition-label">Registered Users</div>
                                    <div className="condition-target">Target: {TGE_TARGETS.users.toLocaleString()}</div>
                                    <div className="tge-progress">
                                        <div
                                            className="tge-progress-bar"
                                            style={{ width: `${userProgress}%` }}
                                        />
                                    </div>
                                    <div className="tge-progress-count">{formatCount(tgeProgress.users, TGE_TARGETS.users)}</div>
                                </div>
                                <div className="condition-card">
                                    <div className="condition-label">Predictions Made</div>
                                    <div className="condition-target">Target: {TGE_TARGETS.predictions.toLocaleString()}</div>
                                    <div className="tge-progress">
                                        <div
                                            className="tge-progress-bar"
                                            style={{ width: `${predictionProgress}%` }}
                                        />
                                    </div>
                                    <div className="tge-progress-count">{formatCount(tgeProgress.predictions, TGE_TARGETS.predictions)}</div>
                                </div>
                            </div>
                        </div>

                        <div className="tge-footer-note">
                            Progress updates display automatically when data is available.
                        </div>

                        <button 
                            className="refresh-btn"
                            onClick={() => {
                                fetchTGEProgress({ force: true });
                            }}
                            style={{ marginTop: '1rem' }}
                        >
                            <RefreshCw size={16} />
                            Check Progress
                        </button>

                        <div className="tge-disclaimer">
                            <span className="tge-disclaimer-icon">⚠️</span>
                            <span>
                                Note: User and prediction counts may decrease if fraudulent activity is detected and removed. This includes self-referrals, bot accounts, duplicate registrations, or gibberish predictions designed to game the system.
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    
    const hasInstantClaimable = parseFloat(claimableAmounts.instant) > 0;
    const hasVestedClaimable = parseFloat(claimableAmounts.vested) > 0;
    const totalClaimable = parseFloat(claimableAmounts.instant) + parseFloat(claimableAmounts.vested);
    const hasWalletBalance = parseFloat(walletBalance) > 0;
    const hasDatabaseBalance = parseFloat(databaseBalance) > 0; // 🆕 Check database balance

    return (
        <div className="token-claim-container">
            <div className="token-claim-card">
                <div className="claim-header">
                    <Coins size={24} />
                    <h3>Token Claim</h3>
                    {tgeActivated && (
                        <div className="tge-badge active">
                            <CheckCircle size={16} />
                            TGE Active
                        </div>
                    )}
                </div>

                {/* 🆕 Database Balance Section - Pending Distribution with Claim Button */}
                {hasDatabaseBalance && (() => {
                    // Calculate pending using wallet balance (more accurate than transaction history)
                    const dbBal = parseFloat(databaseBalance);
                    const walletBal = parseFloat(walletBalance);
                    const alreadyDistributed = Math.max(walletBal, parseFloat(totalClaimed));
                    const pendingDistribution = Math.max(0, dbBal - alreadyDistributed);
                    const isFullyDistributed = pendingDistribution <= 0;
                    
                    return (
                        <div className="database-balance-section">
                            <div className="database-balance-header">
                                <Coins size={20} />
                                <span>Your Earned Rewards</span>
                            </div>
                            <div className="database-balance-amount">
                                {dbBal.toLocaleString()} <span className="token-symbol">TPHILE</span>
                            </div>
                            
                            {/* Always show breakdown */}
                            <div className="claim-breakdown">
                                <div className="breakdown-item">
                                    <span className="breakdown-label">Total Earned:</span>
                                    <span className="breakdown-value">{dbBal.toLocaleString()} TPHILE</span>
                                </div>
                                <div className="breakdown-item">
                                    <span className="breakdown-label">Already in Wallet:</span>
                                    <span className="breakdown-value claimed">{walletBal.toLocaleString()} TPHILE</span>
                                </div>
                                <div className="breakdown-item pending-row">
                                    <span className="breakdown-label">Pending Distribution:</span>
                                    <span className={`breakdown-value ${pendingDistribution > 0 ? 'pending' : 'claimed'}`}>
                                        {pendingDistribution.toLocaleString()} TPHILE
                                    </span>
                                </div>
                            </div>

                            {/* Show distribute button OR fully distributed message */}
                            {!isFullyDistributed ? (
                                <>
                                    <button
                                        className="distribute-btn"
                                        onClick={handleDistribute}
                                        disabled={isDistributing || !walletAddress}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            gap: '12px',
                                            width: '100%',
                                            textAlign: 'center'
                                        }}
                                    >
                                        {isDistributing ? (
                                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', width: '100%' }}>
                                                <Loader className="spinner" size={18} />
                                                <span style={{ flexShrink: 0, textAlign: 'center' }}>Distributing...</span>
                                            </span>
                                        ) : (
                                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', width: '100%' }}>
                                                <TrendingUp size={18} />
                                                <span style={{ flexShrink: 0, textAlign: 'center' }}>Distribute {pendingDistribution.toLocaleString()} TPHILE to My Wallet</span>
                                            </span>
                                        )}
                                    </button>
                                    <p className="distribute-note">
                                        <AlertCircle size={14} />
                                        <span>Click to transfer your earned tokens to your wallet.</span>
                                    </p>
                                </>
                            ) : (
                                <div className="fully-distributed-message">
                                    <CheckCircle size={20} />
                                    <span>All rewards distributed! Check your wallet balance above.</span>
                                </div>
                            )}

                            {/* Claim Status Message */}
                            {claimStatus && (
                                <div className={`claim-status ${
                                    claimStatus.startsWith('✅') ? 'success' : 
                                    claimStatus.startsWith('❌') ? 'error' : 'info'
                                }`}>
                                    <span>{claimStatus}</span>
                                    {claimTxHash && (
                                        <a
                                            href={`${EXPLORER_URL}/tx/${claimTxHash}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="tx-link"
                                        >
                                            <ExternalLink size={14} />
                                            View on Explorer
                                        </a>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* Wallet Balance Section */}
                {hasWalletBalance && (
                    <div className="wallet-balance-section">
                        <div className="wallet-balance-header">
                            <Shield size={20} />
                            <span>Your Wallet Balance</span>
                        </div>
                        <div className="wallet-balance-amount">
                            {parseFloat(walletBalance).toLocaleString()} <span className="token-symbol">TPHILE</span>
                        </div>
                        <p className="wallet-balance-note">Tokens already in your wallet</p>
                        
                        {/* Show button initially, replace with subtle link after click */}
                        {!showTokenAddedConfirmation ? (
                            <button 
                                className="add-token-btn"
                                onClick={addTokenToWallet}
                            >
                                🌙 Add to MetaMask
                            </button>
                        ) : (
                            <>
                                {/* Confirmation message */}
                                <div className="token-added-confirmation">
                                    <CheckCircle size={16} />
                                    <span>Was the token added successfully?</span>
                                </div>
                                
                                {/* Subtle text link to add token again */}
                                <span 
                                    className="add-token-link-subtle"
                                    onClick={addTokenToWallet}
                                >
                                    Didn't add the token? Click here
                                </span>
                            </>
                        )}

                        {/* 🆕 Cash Out Section */}
                        <div className="cash-out-divider"></div>
                        
                        <div className="cash-out-section">
                            <div className="cash-out-header">
                                <DollarSign size={20} />
                                <span>Need Cash?</span>
                            </div>
                            <p className="cash-out-description">
                                Convert your TPH tokens to USDC instantly
                            </p>
                            <button 
                                className="cash-out-btn"
                                onClick={() => navigate('/swap')}
                                disabled={parseFloat(walletBalance) === 0 && parseFloat(usdcBalance) === 0}
                            >
                                <ArrowDownUp size={18} />
                                Swap TPH ⇄ USDC
                            </button>
                        </div>
                    </div>
                )}

                {isLoading ? (
                    <div className="claim-loading">
                        <Loader className="spinner" size={32} />
                        <p>Loading claimable amounts...</p>
                    </div>
                ) : (
                    <>
                        {error && (
                            <div className="claim-alert error">
                                <AlertCircle size={18} />
                                <span>{error}</span>
                            </div>
                        )}

                        {successMessage && (
                            <div className="claim-alert success">
                                <CheckCircle size={18} />
                                <span>{successMessage}</span>
                            </div>
                        )}

                        {txHash && (
                            <div className="tx-link-container">
                                <a
                                    href={`${EXPLORER_URL}/tx/${txHash}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="tx-link"
                                >
                                    <ExternalLink size={14} />
                                    View Transaction on Explorer
                                </a>
                            </div>
                        )}

                        {totalClaimable === 0 ? (
                            <div className="no-claimable-message">
                                <CheckCircle size={32} />
                                <p>No tokens available to claim at this time.</p>
                            </div>
                        ) : (
                            <div className="claim-options">
                                {/* Instant Claim Section */}
                                {hasInstantClaimable && (
                                    <div className="claim-option instant">
                                        <div className="claim-option-header">
                                            <div className="claim-option-info">
                                                <TrendingUp size={20} />
                                                <div>
                                                    <h4>Instant Claim (50%)</h4>
                                                    <p className="claim-amount">
                                                        {parseFloat(claimableAmounts.instant).toLocaleString()} TPHILE
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            className="claim-btn primary"
                                            onClick={claimInstant}
                                            disabled={isClaiming}
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                gap: '12px',
                                                width: '100%'
                                            }}
                                        >
                                            {isClaiming ? (
                                                <>
                                                    <Loader className="spinner" size={18} />
                                                    <span style={{ flexShrink: 0 }}>Claiming...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Coins size={18} />
                                                    <span style={{ flexShrink: 0 }}>Claim Instant</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}

                                {/* Vested Claim Section */}
                                {hasVestedClaimable && (
                                    <div className="claim-option vested">
                                        <div className="claim-option-header">
                                            <div className="claim-option-info">
                                                <Clock size={20} />
                                                <div>
                                                    <h4>Vested Claim (50%)</h4>
                                                    <p className="claim-amount">
                                                        {parseFloat(claimableAmounts.vested).toLocaleString()} TPHILE
                                                    </p>
                                                    <p className="claim-description">
                                                        Unlocked from vesting schedule
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            className="claim-btn secondary"
                                            onClick={claimVested}
                                            disabled={isClaiming}
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                gap: '12px',
                                                width: '100%'
                                            }}
                                        >
                                            {isClaiming ? (
                                                <>
                                                    <Loader className="spinner" size={18} />
                                                    <span style={{ flexShrink: 0 }}>Claiming...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Coins size={18} />
                                                    <span style={{ flexShrink: 0 }}>Claim Vested</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}

                                {/* Total Claimable Summary */}
                                <div className="claim-summary">
                                    <div className="summary-row">
                                        <span>Total Claimable:</span>
                                        <span className="summary-amount">
                                            {totalClaimable.toLocaleString()} TPHILE
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        <button
                            className="refresh-btn"
                            onClick={() => {
                                checkCurrentNetwork();
                                fetchClaimableAmounts();
                                fetchWalletBalance();
                                fetchDatabaseBalance();
                            }}
                            disabled={isLoading || isClaiming}
                        >
                            <RefreshCw className={isLoading ? 'spinner' : ''} size={16} />
                            Refresh
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default TokenClaim;

